import { useLayoutEffect, useState } from 'react';
import useGenerateExternalUrl from 'hooks/useGenerateExternalUrl';

export interface UseFeatureFlagRedirectOptions {
  /**
   * The path to redirect to on the external domain
   */
  redirectPath: string;
  /**
   * Whether the feature flag is enabled (boolean or async function)
   */
  isEnabled: boolean | (() => Promise<boolean>);
  /**
   * Optional callback when redirecting
   */
  onRedirect?: () => void;
  /**
   * Optional callback when not redirecting
   */
  onNoRedirect?: () => void;
}

/**
 * Custom hook that handles feature flag-based domain redirection.
 *
 * - Shows blank screen while checking feature flag
 * - Redirects to external domain if flag is enabled
 * - Shows content if flag is disabled
 * - Manages UI ready state to prevent content flash
 * - Handles both sync boolean flags and async flag functions
 *
 * @param options Configuration options for the redirect behavior
 * @returns Object containing uiReady state and redirect utilities
 */
export const useFeatureFlagRedirect = (
  options: UseFeatureFlagRedirectOptions,
) => {
  const { redirectPath, isEnabled, onRedirect, onNoRedirect } = options;

  const [uiReady, setUiReady] = useState(false);

  const externalUrl = useGenerateExternalUrl(redirectPath);

  useLayoutEffect(() => {
    const checkAndRedirect = async () => {
      try {
        // Handle both boolean and async function
        const isEnabledValue =
          typeof isEnabled === 'function' ? await isEnabled() : isEnabled;

        if (isEnabledValue) {
          if (onRedirect) {
            onRedirect();
          }
          window.location.href = externalUrl;
          return;
        }

        if (onNoRedirect) {
          onNoRedirect();
        }
        setUiReady(true);
      } catch (error) {
        setUiReady(true); // Ensure UI becomes ready even on error
      }
    };

    if (!uiReady) {
      checkAndRedirect();
    }
  }, [externalUrl, isEnabled, onRedirect, onNoRedirect, uiReady]);

  return { uiReady };
};

export default useFeatureFlagRedirect;
